import { z } from 'zod';

import {
  CashExpenseCategoryDtoSchema,
  CashExpenseDtoSchema,
  CashFundingSourceCategoryDtoSchema,
  CashLocationDtoSchema,
  CashMovementDtoSchema,
  CashOperatingConfigurationDtoSchema,
  CashRemittanceDestinationDtoSchema,
  CashScopeModeStateDtoSchema,
  CashCountedDenominationSnapshotDtoSchema,
  CashDenominationInventoryCheckpointApplicationStatusSchema,
  CashDenominationBaselineTypeSchema,
  CashDenominationConfidenceSchema,
  CashDenominationInventoryCheckpointInventoryStatusSchema,
  CashDenominationInventoryCheckpointOriginSchema,
  CashDenominationInventoryCheckpointSourceEventSideSchema,
  CashDenominationInventoryCheckpointSourceEventTypeSchema,
  CashDenominationInventoryCheckpointSourceSchema,
  CashDenominationSourceSchema,
  CashLocationTypeSchema,
  CashSessionDtoSchema,
  CashTreasuryDistributionRequestDtoSchema,
  CashTreasuryDistributionRequestStatusSchema,
  CashTransferDtoSchema,
  CashVarianceActionDtoSchema,
  CashVarianceCaseDtoSchema,
  supportedCashDenominationMinorValues,
} from '../cash/index.js';
import {
  type CashTreasuryDistributionRequestSyncEventType,
  type CashTreasuryExpenseSyncEventType,
  type CashTreasuryFundingSyncEventType,
  type CashSyncDenominationAllocationRevisionDto,
  type CashSyncDenominationBaselineDto,
  type CashSyncDenominationInventoryCheckpointPublicationDto,
  type CashSyncDenominationInventoryCheckpointRowDto,
  type CashSyncDenominationRowDto,
  type CashSyncTreasuryInventoryCheckpointPayloadDto,
  type CashSyncTreasuryInventoryCheckpointStateItem,
  type CashSyncTreasuryInventoryCheckpointsPullRequest,
  type CashSyncTreasuryInventoryCheckpointsPullResponse,
  type CashSyncTreasuryDistributionRequestsPullRequest,
  type CashSyncTreasuryDistributionRequestsPullResponse,
  type CashSyncTreasuryDistributionRequestStateItem,
  type CashSyncOperationEnvelopeDto,
  type CashSyncTreasuryExpensePayloadDto,
  type CashSyncTreasuryExpenseResultDto,
  type CashSyncTreasuryExpenseStateItem,
  type CashSyncTreasuryExpensesPullRequest,
  type CashSyncTreasuryExpensesPullResponse,
  type CashSyncTreasuryFundingPayloadDto,
  type CashSyncTreasuryFundingsPullRequest,
  type CashSyncTreasuryFundingsPullResponse,
  type CashSyncTreasuryFundingStateItem,
  type CashSyncConfigItems,
  type CashSyncConfigPullRequest,
  type CashSyncConfigPullResponse,
  type CashSyncFailureCategory,
  type CashSyncEntityType,
  type CashSyncEntityIdentity,
  type CashSyncPushFailureDetails,
  type CashSyncSessionDto,
  type CashSyncOperatingConfigurationDto,
  type CashSyncRemittanceDestinationDto,
  type CashSyncScopeModeStateDto,
  type CashSyncPushFailure,
  type CashSyncPushItem,
  type CashSyncPushRequest,
  type CashSyncPushResponse,
  cashTreasuryExpenseSyncEventTypeValues,
  cashTreasuryDistributionRequestSyncEventTypeValues,
  cashTreasuryFundingSyncEventTypeValues,
  cashSyncFailureCategoryValues,
  cashSyncEntityTypeValues,
} from './cash-sync.dto.js';
import { cashSyncErrorCodes, type CashSyncErrorCode } from './cash-sync-error-codes.js';
import {
  CashTargetStateSyncEntityTypeSchema,
  CashTargetStateSyncEventTypeSchema,
  CashTargetStateSyncPayloadDtoSchema,
} from './cash-target-state-sync.schema.js';

const timestampSchema = z.iso.datetime();
const supportedDenominations = new Set<number>(supportedCashDenominationMinorValues);

export const CashSyncOperatingConfigurationDtoSchema: z.ZodType<CashSyncOperatingConfigurationDto> =
  z.intersection(
    CashOperatingConfigurationDtoSchema,
    z.object({
      id: z.uuid(),
      updatedByOperationId: z.uuid(),
      createdAt: timestampSchema,
    }),
  );

export const CashSyncScopeModeStateDtoSchema: z.ZodType<CashSyncScopeModeStateDto> = z.intersection(
  CashScopeModeStateDtoSchema,
  z.object({
    id: z.uuid(),
    activeActivationId: z.uuid().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  }),
);

export const CashSyncRemittanceDestinationDtoSchema: z.ZodType<CashSyncRemittanceDestinationDto> =
  z.intersection(
    CashRemittanceDestinationDtoSchema,
    z.object({ revision: z.number().int().positive().max(2_147_483_647) }),
  );

export const CashSyncConfigItemsSchema: z.ZodType<CashSyncConfigItems> = z.object({
  cashOperatingConfiguration: CashSyncOperatingConfigurationDtoSchema,
  cashScopeModeStates: z.array(CashSyncScopeModeStateDtoSchema),
  cashRemittanceDestinations: z.array(CashSyncRemittanceDestinationDtoSchema),
  cashLifecycleEvents: z.array(CashTargetStateSyncPayloadDtoSchema),
  cashLocations: z.array(CashLocationDtoSchema),
  expenseCategories: z.array(CashExpenseCategoryDtoSchema),
  fundingSourceCategories: z.array(CashFundingSourceCategoryDtoSchema),
});

export const CashSyncConfigPullRequestSchema: z.ZodType<CashSyncConfigPullRequest> = z.object({
  cursor: z.string().trim().min(1).nullable().optional(),
});

export const CashSyncConfigPullResponseSchema: z.ZodType<CashSyncConfigPullResponse> = z.object({
  items: CashSyncConfigItemsSchema,
  nextCursor: z.string().trim().min(1).nullable(),
  serverTime: timestampSchema,
});

const CashSyncPushItemBaseSchema = z.object({
  entityId: z.uuid(),
  occurredAt: timestampSchema,
  stationId: z.uuid().nullable().optional(),
});

export const CashSyncSessionDtoSchema: z.ZodType<CashSyncSessionDto> = CashSessionDtoSchema.and(
  z.object({
    closeRequiresDenominations: z.boolean().nullable().optional(),
  }),
);

export const CashSyncPushSessionItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashSession'),
    payload: CashSyncSessionDtoSchema,
  });

export const CashSyncPushMovementItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashMovement'),
    payload: CashMovementDtoSchema,
  });

export const CashSyncPushTransferItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashTransfer'),
    payload: CashTransferDtoSchema,
  });

export const CashSyncPushExpenseItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashExpense'),
    payload: CashExpenseDtoSchema,
  });

export const CashSyncPushVarianceCaseItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashVarianceCase'),
    payload: CashVarianceCaseDtoSchema,
  });

export const CashSyncPushVarianceActionItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashVarianceAction'),
    payload: CashVarianceActionDtoSchema,
  });

export const CashTreasuryDistributionRequestSyncEventTypeSchema: z.ZodType<CashTreasuryDistributionRequestSyncEventType> =
  z.enum(cashTreasuryDistributionRequestSyncEventTypeValues);

export const CashTreasuryFundingSyncEventTypeSchema: z.ZodType<CashTreasuryFundingSyncEventType> =
  z.enum(cashTreasuryFundingSyncEventTypeValues);

export const CashTreasuryExpenseSyncEventTypeSchema: z.ZodType<CashTreasuryExpenseSyncEventType> =
  z.enum(cashTreasuryExpenseSyncEventTypeValues);

export const CashSyncPushDenominationSnapshotItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashDenominationSnapshot'),
    payload: CashCountedDenominationSnapshotDtoSchema,
  });

export const CashSyncDenominationRowDtoSchema: z.ZodType<CashSyncDenominationRowDto> = z
  .object({
    denom: z
      .number()
      .int()
      .positive()
      .refine((value) => supportedDenominations.has(value), 'Unsupported denomination'),
    count: z.number().int().nonnegative(),
    rowTotalMinor: z.number().int().nonnegative(),
  })
  .superRefine((value, context) => {
    if (value.rowTotalMinor !== value.denom * value.count) {
      context.addIssue({
        code: 'custom',
        message: 'rowTotalMinor must equal denom multiplied by count.',
        path: ['rowTotalMinor'],
      });
    }
  });

const CashSyncDenominationRowsSchema = z
  .array(CashSyncDenominationRowDtoSchema)
  .nonempty()
  .superRefine((rows, context) => {
    addDuplicateDenominationIssues(rows, context);
  });

const CashSyncDenominationBaselineRowsSchema = z
  .array(CashSyncDenominationRowDtoSchema)
  .superRefine((rows, context) => {
    addDuplicateDenominationIssues(rows, context);
  });

export const CashSyncDenominationAllocationRevisionDtoSchema: z.ZodType<CashSyncDenominationAllocationRevisionDto> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      allocationId: z.uuid(),
      cashMovementId: z.uuid(),
      cashLocationId: z.uuid(),
      cashSessionId: z.uuid().nullable(),
      storeId: z.uuid().nullable(),
      locationId: z.uuid().nullable(),
      stationId: z.uuid().nullable(),
      currentRevisionId: z.uuid().nullable(),
      movementAmountMinor: z.number().int().nonnegative(),
      movementOccurredAt: timestampSchema,
      source: CashDenominationSourceSchema,
      confidence: CashDenominationConfidenceSchema,
      denominationTotalMinor: z.number().int().nonnegative(),
      effectiveAt: timestampSchema,
      note: z.string().trim().min(1).max(500).nullable(),
      createdByUserId: z.uuid().nullable(),
      createdAt: timestampSchema,
      allocationCreatedAt: timestampSchema,
      allocationUpdatedAt: timestampSchema,
      rows: CashSyncDenominationRowsSchema,
    })
    .superRefine((value, context) => {
      addRowsTotalMismatchIssue(value.rows, value.denominationTotalMinor, context, [
        'denominationTotalMinor',
      ]);
      addRowsTotalMismatchIssue(value.rows, value.movementAmountMinor, context, [
        'movementAmountMinor',
      ]);
    });

export const CashSyncPushDenominationAllocationRevisionItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashDenominationAllocationRevision'),
    payload: CashSyncDenominationAllocationRevisionDtoSchema,
  });

export const CashSyncDenominationBaselineDtoSchema: z.ZodType<CashSyncDenominationBaselineDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    cashLocationId: z.uuid(),
    cashSessionId: z.uuid().nullable(),
    sourceSnapshotId: z.uuid().nullable(),
    sourceCashSessionId: z.uuid().nullable(),
    baselineType: CashDenominationBaselineTypeSchema,
    expectedAmountMinor: z.number().int(),
    countedAmountMinor: z.number().int().nonnegative(),
    varianceAmountMinor: z.number().int(),
    source: z.literal('countedBaseline'),
    confidence: z.literal('countedBaseline'),
    countedAt: timestampSchema,
    effectiveAt: timestampSchema,
    supersededThrough: timestampSchema.nullable(),
    varianceCaseId: z.uuid().nullable(),
    varianceRecognitionMovementId: z.uuid().nullable(),
    note: z.string().trim().min(1).max(500).nullable(),
    createdByUserId: z.uuid().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    rows: CashSyncDenominationBaselineRowsSchema,
  })
  .superRefine((value, context) => {
    addEmptyBaselineRowsIssue(value.rows, value.countedAmountMinor, context);
    if (value.rows.length > 0 || value.countedAmountMinor === 0) {
      addRowsTotalMismatchIssue(value.rows, value.countedAmountMinor, context, [
        'countedAmountMinor',
      ]);
    }

    const expectedVariance = value.countedAmountMinor - value.expectedAmountMinor;
    if (value.varianceAmountMinor !== expectedVariance) {
      context.addIssue({
        code: 'custom',
        message: 'varianceAmountMinor must equal countedAmountMinor minus expectedAmountMinor.',
        path: ['varianceAmountMinor'],
      });
    }

    if (value.baselineType === 'cleanBaseline' && value.varianceAmountMinor !== 0) {
      context.addIssue({
        code: 'custom',
        message: 'Clean baselines must have zero variance.',
        path: ['varianceAmountMinor'],
      });
    }

    if (value.baselineType === 'varianceBaseline' && value.varianceAmountMinor === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Variance baselines must have non-zero variance.',
        path: ['varianceAmountMinor'],
      });
    }
  });

export const CashSyncPushDenominationBaselineItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashDenominationBaseline'),
    payload: CashSyncDenominationBaselineDtoSchema,
  });

export const CashSyncDenominationInventoryCheckpointRowDtoSchema: z.ZodType<CashSyncDenominationInventoryCheckpointRowDto> =
  z
    .object({
      denom: z
        .number()
        .int()
        .positive()
        .refine((value) => supportedDenominations.has(value), 'Unsupported denomination'),
      count: z.number().int().positive(),
      rowTotalMinor: z.number().int().positive(),
    })
    .superRefine((value, context) => {
      if (value.rowTotalMinor !== value.denom * value.count) {
        context.addIssue({
          code: 'custom',
          message: 'rowTotalMinor must equal denom multiplied by count.',
          path: ['rowTotalMinor'],
        });
      }
    });

const CashSyncDenominationInventoryCheckpointRowsSchema = z
  .array(CashSyncDenominationInventoryCheckpointRowDtoSchema)
  .superRefine((rows, context) => {
    addDuplicateDenominationIssues(rows, context);
  });

export const CashSyncDenominationInventoryCheckpointPublicationDtoSchema: z.ZodType<CashSyncDenominationInventoryCheckpointPublicationDto> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      storeId: z.uuid().nullable(),
      locationId: z.uuid().nullable(),
      stationId: z.uuid().nullable(),
      cashLocationId: z.uuid(),
      cashLocationType: CashLocationTypeSchema,
      inventoryStatus: CashDenominationInventoryCheckpointInventoryStatusSchema,
      source: CashDenominationInventoryCheckpointSourceSchema,
      sourceEventType: CashDenominationInventoryCheckpointSourceEventTypeSchema,
      sourceEventId: z.string().trim().min(1).max(200),
      sourceEventSide: CashDenominationInventoryCheckpointSourceEventSideSchema,
      sourceEventOccurredAt: timestampSchema,
      asOf: timestampSchema,
      rows: CashSyncDenominationInventoryCheckpointRowsSchema.nullable(),
      totalMinor: z.number().int().nonnegative().nullable(),
      ledgerBalanceMinor: z.number().int(),
      ledgerMovementCount: z.number().int().nonnegative().nullable(),
      ledgerLastMovementAt: timestampSchema.nullable(),
      ledgerLastMovementUpdatedAt: timestampSchema.nullable(),
      expectedLedgerBalanceMinor: z.number().int().nullable(),
      differenceMinor: z.number().int().nullable(),
      cashSessionId: z.uuid().nullable(),
      lastMovementId: z.uuid().nullable(),
      sourceSnapshotId: z.uuid().nullable(),
      sourceBaselineId: z.uuid().nullable(),
    })
    .superRefine((value, context) => {
      if (value.inventoryStatus === 'unavailable') {
        if (value.rows !== null) {
          context.addIssue({
            code: 'custom',
            message: 'Unavailable inventory checkpoint sync payloads must not include rows.',
            path: ['rows'],
          });
        }
        if (value.totalMinor !== null) {
          context.addIssue({
            code: 'custom',
            message: 'Unavailable inventory checkpoint sync payloads must not include totals.',
            path: ['totalMinor'],
          });
        }
        return;
      }

      if (value.rows === null) {
        context.addIssue({
          code: 'custom',
          message: 'Known inventory checkpoint sync payloads require rows.',
          path: ['rows'],
        });
        return;
      }
      if (value.totalMinor === null) {
        context.addIssue({
          code: 'custom',
          message: 'Known inventory checkpoint sync payloads require totalMinor.',
          path: ['totalMinor'],
        });
        return;
      }

      addRowsTotalMismatchIssue(value.rows, value.totalMinor, context, ['totalMinor']);
    });

export const CashSyncTreasuryInventoryCheckpointPayloadDtoSchema =
  CashSyncDenominationInventoryCheckpointPublicationDtoSchema.and(
    z.object({
      origin: CashDenominationInventoryCheckpointOriginSchema,
      applicationStatus: CashDenominationInventoryCheckpointApplicationStatusSchema,
      publishedAt: timestampSchema,
      updatedAt: timestampSchema,
    }),
  ).superRefine((value, context) => {
    if (value.origin !== 'cloudTreasuryCount') {
      context.addIssue({
        code: 'custom',
        message: 'Treasury inventory checkpoint pulls only support Cloud Treasury count origin.',
        path: ['origin'],
      });
    }

    if (value.applicationStatus !== 'accepted') {
      context.addIssue({
        code: 'custom',
        message: 'Treasury inventory checkpoint pulls only include accepted checkpoints.',
        path: ['applicationStatus'],
      });
    }

    if (
      value.cashLocationType !== 'treasury' ||
      value.storeId !== null ||
      value.locationId !== null ||
      value.stationId !== null
    ) {
      context.addIssue({
        code: 'custom',
        message:
          'Treasury inventory checkpoint pulls require a company-scoped Treasury cash location.',
        path: ['cashLocationId'],
      });
    }

    if (value.source !== 'treasuryCount' || value.sourceEventType !== 'treasuryCount') {
      context.addIssue({
        code: 'custom',
        message: 'Treasury inventory checkpoint pulls require Treasury count source evidence.',
        path: ['source'],
      });
    }

    if (value.sourceEventSide !== 'single') {
      context.addIssue({
        code: 'custom',
        message: 'Treasury inventory checkpoint pulls require single-sided count evidence.',
        path: ['sourceEventSide'],
      });
    }

    if (value.cashSessionId !== null || value.lastMovementId !== null) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury count inventory checkpoints cannot reference sessions or movements.',
        path: ['cashSessionId'],
      });
    }
  }) as z.ZodType<CashSyncTreasuryInventoryCheckpointPayloadDto>;

export const CashSyncTreasuryInventoryCheckpointStateItemSchema: z.ZodType<CashSyncTreasuryInventoryCheckpointStateItem> =
  z.object({
    entityType: z.literal('cashDenominationInventoryCheckpoint'),
    entityId: z.uuid(),
    occurredAt: timestampSchema,
    payload: CashSyncTreasuryInventoryCheckpointPayloadDtoSchema,
  });

export const CashSyncPushDenominationInventoryCheckpointItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: z.literal('cashDenominationInventoryCheckpoint'),
    payload: CashSyncDenominationInventoryCheckpointPublicationDtoSchema,
  });

export const CashSyncPushTargetStateItemSchema: z.ZodType<CashSyncPushItem> =
  CashSyncPushItemBaseSchema.extend({
    entityType: CashTargetStateSyncEntityTypeSchema,
    eventType: CashTargetStateSyncEventTypeSchema,
    payload: CashTargetStateSyncPayloadDtoSchema,
  }).superRefine((value, context) => {
    if (value.entityType !== value.payload.entityType) {
      context.addIssue({
        code: 'custom',
        message: 'Target-state entity type does not match its payload.',
        path: ['entityType'],
      });
    }
    if (value.eventType !== value.payload.eventType) {
      context.addIssue({
        code: 'custom',
        message: 'Target-state event type does not match its payload.',
        path: ['eventType'],
      });
    }
  });

export const CashSyncPushItemSchema = z.union([
  CashSyncPushSessionItemSchema,
  CashSyncPushMovementItemSchema,
  CashSyncPushTransferItemSchema,
  CashSyncPushExpenseItemSchema,
  CashSyncPushVarianceCaseItemSchema,
  CashSyncPushVarianceActionItemSchema,
  CashSyncPushDenominationSnapshotItemSchema,
  CashSyncPushDenominationAllocationRevisionItemSchema,
  CashSyncPushDenominationBaselineItemSchema,
  CashSyncPushDenominationInventoryCheckpointItemSchema,
  CashSyncPushTargetStateItemSchema,
]);

export const CashSyncPushRequestSchema: z.ZodType<CashSyncPushRequest> = z
  .object({
    cursor: z.string().trim().min(1).nullable().optional(),
    batch: z.array(CashSyncPushItemSchema).nonempty(),
  })
  .superRefine((value, context) => {
    const identities = new Set<string>();
    for (const [index, item] of value.batch.entries()) {
      const identity = `${item.entityType}\u0000${item.entityId}`;
      if (identities.has(identity)) {
        context.addIssue({
          code: 'custom',
          message: 'Cash sync request contains a duplicate entity identity',
          path: ['batch', index],
        });
      }
      identities.add(identity);
    }
  });

export const CashSyncErrorCodeSchema: z.ZodType<CashSyncErrorCode> = z.enum(cashSyncErrorCodes);
export const CashSyncFailureCategorySchema: z.ZodType<CashSyncFailureCategory> = z.enum(
  cashSyncFailureCategoryValues,
);

export const CashSyncPushFailureDetailsSchema: z.ZodType<CashSyncPushFailureDetails> = z.object({
  failureCategory: CashSyncFailureCategorySchema,
  invalidActorUserIds: z.array(z.uuid()).min(1).optional(),
  prerequisiteType: z
    .enum([
      'cash_location',
      'cash_operating_configuration',
      'cash_expense_category',
      'cash_funding_source_category',
      'cash_session',
      'cash_variance_case',
      'cash_variance_action',
      'cash_expense',
      'cash_transfer',
      'cash_movement',
      'purchase_committed',
      'purchase_cancellation',
      'cash_custody_receipt',
      'cash_treasury_distribution_request',
      'cash_remittance_destination',
      'cash_denomination_snapshot',
      'cash_denomination_correction',
      'cash_denomination_allocation',
      'cash_denomination_allocation_revision',
    ] as const)
    .optional(),
  prerequisiteId: z.uuid().nullable().optional(),
  replayableAfterFix: z.boolean().optional(),
  upstreamStatus: z.number().int().positive().optional(),
  upstreamCode: z.string().trim().min(1).optional(),
});

export const CashSyncEntityTypeSchema: z.ZodType<CashSyncEntityType> =
  z.enum(cashSyncEntityTypeValues);

export const CashSyncEntityIdentitySchema: z.ZodType<CashSyncEntityIdentity> = z
  .object({
    entityType: CashSyncEntityTypeSchema,
    entityId: z.uuid(),
  })
  .strict();

export const CashSyncPushFailureSchema: z.ZodType<CashSyncPushFailure> = z
  .object({
    entityType: CashSyncEntityTypeSchema,
    entityId: z.uuid(),
    errorCode: CashSyncErrorCodeSchema,
    message: z.string().trim().min(1).optional(),
    details: CashSyncPushFailureDetailsSchema.optional(),
  })
  .strict();

export const CashSyncPushResponseSchema: z.ZodType<CashSyncPushResponse> = z
  .object({
    acked: z.array(CashSyncEntityIdentitySchema),
    failed: z.array(CashSyncPushFailureSchema),
  })
  .strict()
  .superRefine((value, context) => {
    const acknowledged = new Set<string>();
    for (const [index, identity] of value.acked.entries()) {
      const key = `${identity.entityType}\u0000${identity.entityId}`;
      if (acknowledged.has(key)) {
        context.addIssue({
          code: 'custom',
          message: 'Cash sync response contains a duplicate acknowledgement identity',
          path: ['acked', index],
        });
      }
      acknowledged.add(key);
    }

    const failed = new Set<string>();
    for (const [index, failure] of value.failed.entries()) {
      const key = `${failure.entityType}\u0000${failure.entityId}`;
      if (failed.has(key)) {
        context.addIssue({
          code: 'custom',
          message: 'Cash sync response contains a duplicate failure identity',
          path: ['failed', index],
        });
      }
      if (acknowledged.has(key)) {
        context.addIssue({
          code: 'custom',
          message: 'Cash sync response identity cannot be both acknowledged and failed',
          path: ['failed', index],
        });
      }
      failed.add(key);
    }
  });

export const CashSyncTreasuryDistributionRequestStateItemSchema: z.ZodType<CashSyncTreasuryDistributionRequestStateItem> =
  z.object({
    entityType: z.literal('cashTreasuryDistributionRequest'),
    entityId: z.uuid(),
    eventType: CashTreasuryDistributionRequestSyncEventTypeSchema,
    status: CashTreasuryDistributionRequestStatusSchema,
    stationId: z.uuid(),
    occurredAt: timestampSchema,
    payload: CashTreasuryDistributionRequestDtoSchema,
  });

export const CashSyncTreasuryDistributionRequestsPullRequestSchema: z.ZodType<CashSyncTreasuryDistributionRequestsPullRequest> =
  z
    .object({
      cursor: z.string().trim().min(1).nullable().optional(),
      stationId: z.uuid(),
    })
    .strict();

export const CashSyncTreasuryDistributionRequestsPullResponseSchema: z.ZodType<CashSyncTreasuryDistributionRequestsPullResponse> =
  z.object({
    items: z.array(CashSyncTreasuryDistributionRequestStateItemSchema),
    nextCursor: z.string().trim().min(1).nullable(),
    serverTime: timestampSchema,
  });

export const CashSyncOperationEnvelopeDtoSchema: z.ZodType<CashSyncOperationEnvelopeDto> = z.object(
  {
    operationId: z.uuid(),
    requestHash: z.string().regex(/^[a-f0-9]{64}$/u),
    companyId: z.uuid(),
    scopeType: z.enum(['location', 'treasury']),
    scopeId: z.uuid(),
    actorUserId: z.uuid(),
    resultEntityType: z.string().trim().min(1).max(100),
    resultEntityId: z.uuid(),
    occurredAt: timestampSchema,
    createdAt: timestampSchema,
  },
);

export const CashSyncTreasuryFundingPayloadDtoSchema: z.ZodType<CashSyncTreasuryFundingPayloadDto> =
  z
    .object({
      cashMovement: CashMovementDtoSchema,
      operation: CashSyncOperationEnvelopeDtoSchema,
      denominationAllocationRevision:
        CashSyncDenominationAllocationRevisionDtoSchema.nullable().optional(),
    })
    .superRefine((value, context) => {
      const movement = value.cashMovement;
      if (
        movement.movementKind !== 'funding' ||
        movement.direction !== 'in' ||
        movement.isReversal ||
        movement.reversalOfMovementId !== null ||
        movement.sourceType !== 'funding' ||
        movement.sourceId !== null ||
        movement.storeId !== null ||
        movement.locationId !== null ||
        movement.stationId !== null ||
        movement.cashSessionId !== null ||
        movement.transferId !== null ||
        movement.expenseId !== null ||
        !movement.fundingSourceType
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Treasury funding sync payload must contain only a company-scoped funding movement.',
          path: ['cashMovement'],
        });
      }

      validateTreasuryOperationEnvelope(
        value.operation,
        {
          companyId: movement.companyId,
          scopeId: movement.cashLocationId,
          actorUserId: null,
          resultEntityType: 'cashTreasuryFunding',
          resultEntityId: movement.id,
          occurredAt: movement.occurredAt,
        },
        context,
        ['operation'],
      );

      const allocation = value.denominationAllocationRevision;
      if (!allocation) {
        return;
      }

      if (
        allocation.companyId !== movement.companyId ||
        allocation.cashMovementId !== movement.id ||
        allocation.cashLocationId !== movement.cashLocationId ||
        allocation.currentRevisionId !== allocation.id ||
        allocation.storeId !== null ||
        allocation.locationId !== null ||
        allocation.stationId !== null ||
        allocation.cashSessionId !== null ||
        allocation.movementAmountMinor !== movement.amount ||
        allocation.movementOccurredAt !== movement.occurredAt
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Treasury funding denomination allocation must match its movement.',
          path: ['denominationAllocationRevision'],
        });
      }
    });

export const CashSyncTreasuryFundingStateItemSchema: z.ZodType<CashSyncTreasuryFundingStateItem> = z
  .object({
    entityType: z.literal('cashTreasuryFunding'),
    entityId: z.uuid(),
    eventType: CashTreasuryFundingSyncEventTypeSchema,
    occurredAt: timestampSchema,
    payload: CashSyncTreasuryFundingPayloadDtoSchema,
  })
  .superRefine((value, context) => {
    if (value.entityId !== value.payload.cashMovement.id) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury funding state identity must match its movement.',
        path: ['entityId'],
      });
    }
    const expectedStreamTimestamp = latestTimestamp([
      value.payload.cashMovement.updatedAt,
      value.payload.denominationAllocationRevision?.allocationUpdatedAt ?? null,
    ]);
    if (value.occurredAt !== expectedStreamTimestamp) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury funding state timestamp must include allocation revisions.',
        path: ['occurredAt'],
      });
    }
  });

export const CashSyncTreasuryFundingsPullRequestSchema: z.ZodType<CashSyncTreasuryFundingsPullRequest> =
  z
    .object({
      cursor: z.string().trim().min(1).nullable().optional(),
    })
    .strict();

export const CashSyncTreasuryFundingsPullResponseSchema: z.ZodType<CashSyncTreasuryFundingsPullResponse> =
  z.object({
    items: z.array(CashSyncTreasuryFundingStateItemSchema),
    nextCursor: z.string().trim().min(1).nullable(),
    serverTime: timestampSchema,
  });

const CashSyncTreasuryExpenseResultShape = {
  cashExpense: CashExpenseDtoSchema,
  cashMovement: CashMovementDtoSchema,
  reversalMovement: CashMovementDtoSchema.nullable().optional(),
  denominationAllocationRevision:
    CashSyncDenominationAllocationRevisionDtoSchema.nullable().optional(),
  reversalDenominationAllocationRevision:
    CashSyncDenominationAllocationRevisionDtoSchema.nullable().optional(),
  creationOperation: CashSyncOperationEnvelopeDtoSchema,
  transitionOperation: CashSyncOperationEnvelopeDtoSchema.nullable().optional(),
};

export const CashSyncTreasuryExpenseResultDtoSchema: z.ZodType<CashSyncTreasuryExpenseResultDto> = z
  .object(CashSyncTreasuryExpenseResultShape)
  .superRefine((value, context) => {
    validateTreasuryExpenseResult(value, context, []);
  });

export const CashSyncTreasuryExpensePayloadDtoSchema: z.ZodType<CashSyncTreasuryExpensePayloadDto> =
  z
    .object({
      ...CashSyncTreasuryExpenseResultShape,
      predecessorResults: z.array(CashSyncTreasuryExpenseResultDtoSchema),
    })
    .superRefine((value, context) => {
      validateTreasuryExpenseResult(value, context, []);
      validateTreasuryExpensePredecessorChain(value, context);
    });

export const CashSyncTreasuryExpenseStateItemSchema: z.ZodType<CashSyncTreasuryExpenseStateItem> = z
  .object({
    entityType: z.literal('cashTreasuryExpense'),
    entityId: z.uuid(),
    eventType: CashTreasuryExpenseSyncEventTypeSchema,
    occurredAt: timestampSchema,
    payload: CashSyncTreasuryExpensePayloadDtoSchema,
  })
  .superRefine((value, context) => {
    if (value.entityId !== value.payload.cashExpense.id) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury expense state identity must match its terminal expense.',
        path: ['entityId'],
      });
    }
    const expectedEventType =
      value.payload.cashExpense.status === 'voided'
        ? 'cashTreasuryExpenseVoided'
        : 'cashTreasuryExpenseCreated';
    if (value.eventType !== expectedEventType) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury expense state event must match the terminal expense status.',
        path: ['eventType'],
      });
    }
    const expectedStreamTimestamp = latestTreasuryExpensePayloadTimestamp(value.payload);
    if (value.occurredAt !== expectedStreamTimestamp) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury expense state timestamp must include every aggregate revision.',
        path: ['occurredAt'],
      });
    }
  });

export const CashSyncTreasuryExpensesPullRequestSchema: z.ZodType<CashSyncTreasuryExpensesPullRequest> =
  z
    .object({
      cursor: z.string().trim().min(1).nullable().optional(),
    })
    .strict();

export const CashSyncTreasuryExpensesPullResponseSchema: z.ZodType<CashSyncTreasuryExpensesPullResponse> =
  z.object({
    items: z.array(CashSyncTreasuryExpenseStateItemSchema),
    nextCursor: z.string().trim().min(1).nullable(),
    serverTime: timestampSchema,
  });

export const CashSyncTreasuryInventoryCheckpointsPullRequestSchema: z.ZodType<CashSyncTreasuryInventoryCheckpointsPullRequest> =
  z
    .object({
      cursor: z.string().trim().min(1).nullable().optional(),
    })
    .strict();

export const CashSyncTreasuryInventoryCheckpointsPullResponseSchema: z.ZodType<CashSyncTreasuryInventoryCheckpointsPullResponse> =
  z.object({
    items: z.array(CashSyncTreasuryInventoryCheckpointStateItemSchema),
    nextCursor: z.string().trim().min(1).nullable(),
    serverTime: timestampSchema,
  });

interface ExpectedTreasuryOperationEnvelope {
  companyId: string;
  scopeId: string;
  actorUserId: string | null;
  resultEntityType: string;
  resultEntityId: string;
  occurredAt: string;
}

function validateTreasuryOperationEnvelope(
  operation: CashSyncOperationEnvelopeDto,
  expected: ExpectedTreasuryOperationEnvelope,
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  if (
    operation.companyId !== expected.companyId ||
    operation.scopeType !== 'treasury' ||
    operation.scopeId !== expected.scopeId ||
    (expected.actorUserId !== null && operation.actorUserId !== expected.actorUserId) ||
    operation.resultEntityType !== expected.resultEntityType ||
    operation.resultEntityId !== expected.resultEntityId ||
    operation.occurredAt !== expected.occurredAt
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury sync operation envelope does not match its durable result.',
      path,
    });
  }
}

function validateTreasuryExpenseResult(
  value: CashSyncTreasuryExpenseResultDto,
  context: z.RefinementCtx,
  pathPrefix: (string | number)[],
): void {
  const expense = value.cashExpense;
  const movement = value.cashMovement;
  const reversal = value.reversalMovement ?? null;
  const transitionOperation = value.transitionOperation ?? null;

  if (
    expense.sourceType !== 'treasury' ||
    expense.storeId !== null ||
    expense.locationId !== null ||
    expense.stationId !== null ||
    expense.cashSessionId !== null ||
    expense.operationId === null ||
    expense.requestHash === null
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense sync payload must contain a company-scoped mutation identity.',
      path: [...pathPrefix, 'cashExpense'],
    });
  }

  if (
    movement.id !== expense.movementId ||
    movement.companyId !== expense.companyId ||
    movement.cashLocationId !== expense.cashLocationId ||
    movement.amount !== expense.amount ||
    movement.description !== expense.description ||
    movement.occurredAt !== expense.occurredAt ||
    movement.movementKind !== 'expense' ||
    movement.direction !== 'out' ||
    movement.isReversal ||
    movement.reversalOfMovementId !== null ||
    movement.storeId !== null ||
    movement.locationId !== null ||
    movement.stationId !== null ||
    movement.cashSessionId !== null ||
    movement.transferId !== null ||
    movement.expenseId !== expense.id ||
    movement.sourceType !== 'expense' ||
    movement.sourceId !== expense.id
  ) {
    context.addIssue({
      code: 'custom',
      message:
        'Treasury expense sync payload must contain the exact original company-scoped movement.',
      path: [...pathPrefix, 'cashMovement'],
    });
  }

  const expectedCreationResultType = expense.replacesExpenseId
    ? 'cashExpenseReplacement'
    : 'cashExpenseCreate';
  validateTreasuryOperationEnvelope(
    value.creationOperation,
    {
      companyId: expense.companyId,
      scopeId: expense.cashLocationId,
      actorUserId: expense.createdByUserId,
      resultEntityType: expectedCreationResultType,
      resultEntityId: expense.id,
      occurredAt: expense.occurredAt,
    },
    context,
    [...pathPrefix, 'creationOperation'],
  );
  if (
    expense.operationId !== value.creationOperation.operationId ||
    expense.requestHash !== value.creationOperation.requestHash
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense creation identity must match its durable operation.',
      path: [...pathPrefix, 'creationOperation'],
    });
  }

  validateTreasuryExpenseAllocation(
    value.denominationAllocationRevision ?? null,
    movement,
    context,
    [...pathPrefix, 'denominationAllocationRevision'],
  );

  if (expense.status === 'active') {
    if (
      reversal !== null ||
      expense.reversalMovementId !== null ||
      expense.voidedByUserId !== null ||
      expense.voidReason !== null ||
      expense.voidedAt !== null ||
      transitionOperation !== null ||
      value.reversalDenominationAllocationRevision
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Active Treasury expenses cannot contain transition or reversal evidence.',
        path: pathPrefix,
      });
    }
    return;
  }

  if (
    !reversal ||
    expense.reversalMovementId === null ||
    expense.voidedByUserId === null ||
    expense.voidReason === null ||
    expense.voidedAt === null ||
    !transitionOperation
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Voided Treasury expenses require complete transition and reversal evidence.',
      path: pathPrefix,
    });
    return;
  }

  if (
    reversal.id !== expense.reversalMovementId ||
    reversal.companyId !== expense.companyId ||
    reversal.cashLocationId !== expense.cashLocationId ||
    reversal.amount !== expense.amount ||
    reversal.description !== expense.voidReason ||
    reversal.occurredAt !== expense.voidedAt ||
    reversal.movementKind !== 'expense' ||
    reversal.direction !== 'in' ||
    !reversal.isReversal ||
    reversal.reversalOfMovementId !== movement.id ||
    reversal.storeId !== null ||
    reversal.locationId !== null ||
    reversal.stationId !== null ||
    reversal.cashSessionId !== null ||
    reversal.transferId !== null ||
    reversal.expenseId !== expense.id ||
    reversal.sourceType !== 'expense' ||
    reversal.sourceId !== expense.id
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense reversal does not exactly reverse its original movement.',
      path: [...pathPrefix, 'reversalMovement'],
    });
  }

  if (
    transitionOperation.operationId === value.creationOperation.operationId ||
    (transitionOperation.resultEntityType !== 'cashExpenseVoid' &&
      transitionOperation.resultEntityType !== 'cashExpenseReplacement')
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense transition operation has an invalid mutation identity.',
      path: [...pathPrefix, 'transitionOperation'],
    });
  }
  validateTreasuryOperationEnvelope(
    transitionOperation,
    {
      companyId: expense.companyId,
      scopeId: expense.cashLocationId,
      actorUserId: expense.voidedByUserId,
      resultEntityType: transitionOperation.resultEntityType,
      resultEntityId:
        transitionOperation.resultEntityType === 'cashExpenseVoid'
          ? expense.id
          : transitionOperation.resultEntityId,
      occurredAt: expense.voidedAt,
    },
    context,
    [...pathPrefix, 'transitionOperation'],
  );
  if (
    transitionOperation.resultEntityType === 'cashExpenseReplacement' &&
    transitionOperation.resultEntityId === expense.id
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Replacement transition must identify the successor expense.',
      path: [...pathPrefix, 'transitionOperation', 'resultEntityId'],
    });
  }

  validateTreasuryExpenseAllocation(
    value.reversalDenominationAllocationRevision ?? null,
    reversal,
    context,
    [...pathPrefix, 'reversalDenominationAllocationRevision'],
  );
}

function validateTreasuryExpensePredecessorChain(
  value: CashSyncTreasuryExpensePayloadDto,
  context: z.RefinementCtx,
): void {
  const predecessors = value.predecessorResults;
  if (value.transitionOperation?.resultEntityType === 'cashExpenseReplacement') {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense state must identify the terminal replacement-chain result.',
      path: ['transitionOperation'],
    });
  }
  if (value.cashExpense.replacesExpenseId === null) {
    if (predecessors.length > 0) {
      context.addIssue({
        code: 'custom',
        message: 'A non-replacement Treasury expense cannot include predecessors.',
        path: ['predecessorResults'],
      });
    }
    return;
  }

  if (predecessors.length === 0 || predecessors[0]?.cashExpense.replacesExpenseId !== null) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense replacement payloads require a complete chain from the root.',
      path: ['predecessorResults'],
    });
    return;
  }

  const seen = new Set<string>();
  const chain = [...predecessors, value];
  for (let index = 0; index < chain.length; index += 1) {
    const current = chain.at(index);
    if (!current || seen.has(current.cashExpense.id)) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury expense replacement chain contains a duplicate expense.',
        path: ['predecessorResults', Math.min(index, predecessors.length - 1)],
      });
      return;
    }
    seen.add(current.cashExpense.id);
    if (
      current.cashExpense.companyId !== value.cashExpense.companyId ||
      current.cashExpense.cashLocationId !== value.cashExpense.cashLocationId
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury expense replacement chain cannot cross company or Treasury scope.',
        path: ['predecessorResults', index],
      });
    }

    const successor = chain.at(index + 1);
    if (!successor) {
      continue;
    }
    const transition = current.transitionOperation ?? null;
    if (
      current.cashExpense.status !== 'voided' ||
      successor.cashExpense.replacesExpenseId !== current.cashExpense.id ||
      !transition ||
      transition.resultEntityType !== 'cashExpenseReplacement' ||
      transition.resultEntityId !== successor.cashExpense.id ||
      !operationEnvelopesMatch(transition, successor.creationOperation)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Treasury expense replacement chain is missing an exact successor transition.',
        path: ['predecessorResults', index],
      });
    }
  }
}

function operationEnvelopesMatch(
  left: CashSyncOperationEnvelopeDto,
  right: CashSyncOperationEnvelopeDto,
): boolean {
  return (
    left.operationId === right.operationId &&
    left.requestHash === right.requestHash &&
    left.companyId === right.companyId &&
    left.scopeType === right.scopeType &&
    left.scopeId === right.scopeId &&
    left.actorUserId === right.actorUserId &&
    left.resultEntityType === right.resultEntityType &&
    left.resultEntityId === right.resultEntityId &&
    left.occurredAt === right.occurredAt &&
    left.createdAt === right.createdAt
  );
}

function latestTreasuryExpensePayloadTimestamp(payload: CashSyncTreasuryExpensePayloadDto): string {
  return latestTimestamp(
    [...payload.predecessorResults, payload].flatMap((result) => [
      result.cashExpense.updatedAt,
      result.cashMovement.updatedAt,
      result.reversalMovement?.updatedAt ?? null,
      result.denominationAllocationRevision?.allocationUpdatedAt ?? null,
      result.reversalDenominationAllocationRevision?.allocationUpdatedAt ?? null,
    ]),
  );
}

function latestTimestamp(values: Array<string | null>): string {
  const timestamps = values.filter((value): value is string => value !== null);
  return timestamps.reduce((latest, value) =>
    new Date(value).getTime() > new Date(latest).getTime() ? value : latest,
  );
}

function addDuplicateDenominationIssues(
  rows: readonly CashSyncDenominationRowDto[],
  context: z.RefinementCtx,
): void {
  const seen = new Set<number>();
  for (const [index, row] of rows.entries()) {
    if (seen.has(row.denom)) {
      context.addIssue({
        code: 'custom',
        message: 'Duplicate denomination rows are not supported.',
        path: [index, 'denom'],
      });
      continue;
    }
    seen.add(row.denom);
  }
}

function addEmptyBaselineRowsIssue(
  rows: readonly CashSyncDenominationRowDto[],
  countedAmountMinor: number,
  context: z.RefinementCtx,
): void {
  if (rows.length > 0 || countedAmountMinor === 0) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Empty denomination baseline rows are only valid for an explicit zero count.',
    path: ['rows'],
  });
}

function validateTreasuryExpenseAllocation(
  allocation: CashSyncDenominationAllocationRevisionDto | null,
  movement: {
    id: string;
    companyId: string;
    cashLocationId: string;
    amount: number;
    occurredAt: string;
  },
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  if (!allocation) {
    return;
  }

  if (
    allocation.companyId !== movement.companyId ||
    allocation.cashMovementId !== movement.id ||
    allocation.cashLocationId !== movement.cashLocationId ||
    allocation.currentRevisionId !== allocation.id ||
    allocation.storeId !== null ||
    allocation.locationId !== null ||
    allocation.stationId !== null ||
    allocation.cashSessionId !== null ||
    allocation.movementAmountMinor !== movement.amount ||
    allocation.movementOccurredAt !== movement.occurredAt
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Treasury expense denomination allocation must match its movement.',
      path,
    });
  }
}

function addRowsTotalMismatchIssue(
  rows: readonly CashSyncDenominationRowDto[],
  expectedAmountMinor: number,
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  const total = rows.reduce((sum, row) => sum + row.rowTotalMinor, 0);
  if (total === expectedAmountMinor) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Denomination row total does not match the expected amount.',
    path,
  });
}
